import { AnimatePresence, motion, useAnimation } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { PagesButton } from "../pagesComponents/PagesButton";
import Site, {
  ContentContainer,
  HeaderDescription,
  Detail2Content,
  DetailContent,
  EnrichContent,
  HeadBot,
  HeadContent,
  Header,
  HeaderVideosGrid,
  ImageContainer,
  LockContent,
  Section,
  ShareContent,
  ShophomeContent,
  TeachContent,
  HeaderTextContent,
  HeaderBrandclubVideo,
  H2,
  H3,
  P,
  Span,
  Img,
} from "./aboutStyles";
import {
  ABOUT_PAGE_HEADER_VIDEOS,
  ABOUT_PAGE_VIDEOS,
  VideoDetails,
} from "./constants";
import { joinClassNames } from "@/utils/StringUtils";
import VideosSection from "./VideosSection";

interface DetailSectionProps {
  buttonStyle?: React.CSSProperties;
  buttonText: string;
  buttonOnClick: () => void;
  className?: string;
  title?: string;
  desc?: string;
  imgUrl?: string;
}

interface TeachTileProps {
  header: string;
  desc: string;
  margintop: number;
  backgroundColor: string;
  className?: string;
}
const TeachTile = ({
  header,
  desc,
  margintop,
  backgroundColor,
  className = "",
}: TeachTileProps) => {
  const controls = useAnimation();
  const [ref, inView] = useInView();
  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);
  const renderDownloadImg = () => {
    return (
      <Img
        ref={ref}
        animate={controls}
        initial="hidden"
        transition={{ duration: 1, ease: "easeInOut" }}
        variants={{
          visible: { opacity: 1, rotate: 0, scale: 1 },
          hidden: { opacity: 0, rotate: 20, scale: 0.5 },
        }}
        alt="download"
        src="https://media.brandclub.com/brandclub/image_asset/teach_download.png"
        className="download_img"
      />
    );
  };
  const renderCashImg = () => {
    return (
      <Img
        ref={ref}
        animate={controls}
        initial="hidden"
        transition={{ duration: 1, ease: "easeInOut" }}
        variants={{
          visible: { opacity: 1, y: 0 },
          hidden: { opacity: 0, y: 100 },
        }}
        alt="cash"
        src="https://media.brandclub.com/brandclub/image_asset/teach_cash.png"
        className="cash_img"
      />
    );
  };
  const renderRetailerImage = () => {
    return (
      <>
        <Img
          ref={ref}
          animate={controls}
          initial="hidden"
          transition={{ duration: 1, ease: "easeInOut" }}
          variants={{
            visible: { opacity: 1, scale: 1, rotate: 0 },
            hidden: { opacity: 0, scale: 0.5, rotate: 80 },
          }}
          alt="cash"
          src="https://media.brandclub.com/brandclub/image_asset/teach_amazon.png"
          className="retailer_img1"
        />
        <Img
          ref={ref}
          animate={controls}
          initial="hidden"
          transition={{ duration: 1, ease: "easeInOut" }}
          variants={{
            visible: { opacity: 1, scale: 1, rotate: 0 },
            hidden: { opacity: 0, scale: 0.5, rotate: 80 },
          }}
          alt="cash"
          src="https://media.brandclub.com/brandclub/image_asset/teach_walmart.png"
          className="retailer_img2"
        />
        <Img
          ref={ref}
          animate={controls}
          initial="hidden"
          transition={{ duration: 1, ease: "easeInOut" }}
          variants={{
            visible: { opacity: 1, scale: 1, rotate: 0 },
            hidden: { opacity: 0, scale: 0.5, rotate: 80 },
          }}
          alt="cash"
          src="https://media.brandclub.com/brandclub/image_asset/teach_target.png"
          className="retailer_img3"
        />
      </>
    );
  };
  const renderSocialMediaImage = () => {
    return (
      <Img
        ref={ref}
        animate={controls}
        initial="hidden"
        transition={{ duration: 1, ease: "easeInOut" }}
        variants={{
          visible: { opacity: 1, scale: 1, rotate: 0 },
          hidden: { opacity: 0, scale: 0.5, rotate: 80 },
        }}
        alt="cash"
        src="https://media.brandclub.com/brandclub/image_asset/influencer_medias.png"
        className="retailer_img1"
      />
    );
  };
  const renderInfluencerPhone = () => {
    return (
      <Img
        ref={ref}
        animate={controls}
        initial="hidden"
        transition={{ duration: 1, ease: "easeInOut" }}
        variants={{
          visible: { opacity: 1, rotate: 0, scale: 1 },
          hidden: { opacity: 0, rotate: 20, scale: 0.5 },
        }}
        alt="download"
        src="https://media.brandclub.com/brandclub/image_asset/influencer_phoneapp.png"
        className="download_img"
      />
    );
  };
  return (
    <div
      className={joinClassNames("tile", className)}
      style={{
        marginTop: margintop,
      }}
    >
      <div className="img">
        <div className="img_bg" style={{ backgroundColor }} />
        <div className="tophalf"></div>
        {header === "Earn" && renderCashImg()}
        {header === "Get cash back" && renderCashImg()}
        <div className="bothalf"></div>
        {header === "Download Brandclub" && renderDownloadImg()}
        {header === "Select" && renderInfluencerPhone()}
        {header === "Shop & engage" && renderRetailerImage()}
        {header === "Share" && renderSocialMediaImage()}
      </div>
      <H3 className="header">{header}</H3>
      <P className={`desc ${header}`}>{desc}</P>
    </div>
  );
};

interface ShopCardProps {
  title: string;
  imgUrl: string;
  desc: string;
}
const ShopCard = ({ title, imgUrl, desc }: ShopCardProps) => {
  return (
    <motion.div className="shop_card">
      <H3 className="title_container">
        <i className="icon">
          <Img src={imgUrl} alt={title} />
        </i>
        <Span className="title">{title}</Span>
      </H3>
      <P className="desc">{desc}</P>
    </motion.div>
  );
};

interface EnrichCardProps {
  imgUrl: string;
  desc: string;
  imageHeight?: number;
  style?: React.CSSProperties;
}
const EnrichCard = ({ imgUrl, desc, imageHeight, style }: EnrichCardProps) => {
  return (
    <motion.div className="enrich_card" style={style}>
      <div className="tag">
        <Img src={imgUrl} alt={imgUrl} height={imageHeight} />
      </div>
      <P className="desc">{desc}</P>
    </motion.div>
  );
};

const HeaderVideos = ({ videos }: HeadSectionProps) => {
  const [mainVideoIndex, setMainVideoIndex] = useState(0);

  return (
    <ImageContainer>
      <HeaderVideosGrid>
        {videos.map(({ posterUrl, video }, index) => {
          const isMain = index === mainVideoIndex;
          const isPreview = !isMain;
          return (
            <AnimatePresence mode="popLayout" key={video}>
              <HeaderBrandclubVideo
                key={video}
                transition={{ duration: 0.3 }}
                exit={{ opacity: 0, y: -10 }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                main={isMain}
                onClick={isPreview ? () => setMainVideoIndex(index) : undefined}
                canPlay={isMain}
                posterUrl={posterUrl}
                videoUrl={video}
              />
            </AnimatePresence>
          );
        })}
      </HeaderVideosGrid>
    </ImageContainer>
  );
};
interface HeadSectionProps {
  videos: VideoDetails[];
}
const HeadSection = ({ videos }: HeadSectionProps) => {
  return (
    <Section>
      <ContentContainer>
        <HeadContent>
          <HeadBot>
            <HeaderTextContent
              animate={{ opacity: [0, 1], y: [100, 0] }}
              transition={{ ease: "easeInOut", duration: 1 }}
              className="text"
            >
              <Header>About us</Header>
              <HeaderDescription>
                We reimagined the relationship between consumers and brands. Now
                we're on a mission to connect people with the world's best
                brands and create more rewarding shopping experiences.
              </HeaderDescription>
            </HeaderTextContent>
            <HeaderVideos videos={videos} />
          </HeadBot>
        </HeadContent>
      </ContentContainer>
    </Section>
  );
};
const HomeTeachSection = () => {
  return (
    <Section>
      <ContentContainer>
        <TeachContent>
          <H2 className="teach_top">How it works</H2>
          <div className="teach_bot">
            <TeachTile
              margintop={0}
              backgroundColor="#FFC5C566"
              header="Download Brandclub"
              desc="Create an account online or through our apple or android app."
              className="tile1"
            />
            <TeachTile
              margintop={0}
              backgroundColor="#E5E5E566"
              header="Shop & engage"
              desc="Engage with your favorite brands while shopping online or in-store."
              className="tile2"
            />
            <TeachTile
              margintop={0}
              backgroundColor="#4E74D066"
              header="Earn"
              desc="Earn real cash. No confusing point systems or gift cards."
              className="tile3"
            />
          </div>
        </TeachContent>
      </ContentContainer>
    </Section>
  );
};

const Detail1Section = ({
  buttonText,
  buttonStyle,
  buttonOnClick,
  className,
  title,
  desc,
  imgUrl,
}: DetailSectionProps) => {
  return (
    <Section className={className}>
      <ContentContainer>
        <DetailContent className={className}>
          <motion.div className="detail_left">
            <H2 className="header">
              {title || "Unify all rewards into a single app"}
            </H2>
            <P className="desc">
              {desc ||
                "Connect our rewards programs from your favorite brands into a single, beautiful app. Get rewarded to shop your favorite retailers, such as Amazon, Walmart, Target and your favorite brand sites."}
            </P>
            {buttonText && buttonOnClick && (
              <PagesButton style={buttonStyle} onClick={buttonOnClick}>
                {buttonText}
              </PagesButton>
            )}
          </motion.div>
          <motion.div className="detail_right">
            <Img
              className="phone_img_bot"
              src={
                imgUrl ||
                "https://media.brandclub.com/brandclub/image_asset/detail_phone5.webp"
              }
              alt="detail_phone1_top"
            />
          </motion.div>
        </DetailContent>
      </ContentContainer>
    </Section>
  );
};

const Detail2Section = ({
  buttonText,
  buttonStyle,
  buttonOnClick,
  className,
  title,
  desc,
  imgUrl,
}: DetailSectionProps) => {
  return (
    <Section className={className}>
      <ContentContainer>
        <Detail2Content className={className}>
          <motion.div className="detail2_left">
            <Img
              className="phone_img_top"
              src={
                imgUrl ||
                "https://media.brandclub.com/brandclub/image_asset/detail_phone_4.webp"
              }
              alt="detail_phone2_top"
            />
          </motion.div>
          <motion.div className="detail2_right">
            <H2 className="header">{title || "Unlimited cash earnings"}</H2>
            <P className="desc">
              {desc ||
                "Earn real cash. No points. No gimmicks. You earn real cash back every time you shop and engage with your favorite brands."}
            </P>
            {buttonText && buttonOnClick && (
              <PagesButton style={buttonStyle} onClick={buttonOnClick}>
                {buttonText}
              </PagesButton>
            )}
          </motion.div>
        </Detail2Content>
      </ContentContainer>
    </Section>
  );
};

const Detail3Section = ({
  buttonOnClick,
  buttonStyle,
  buttonText,
  className,
  title,
  desc,
  imgUrl,
}: DetailSectionProps) => {
  return (
    <Section className={className}>
      <ContentContainer>
        <DetailContent className={joinClassNames("detailsec3", className)}>
          <motion.div className="detail_left">
            <H2 className="header">
              {title || "Earn rewards to advocate for your favorite brands"}
            </H2>
            <P className="desc">
              {desc ||
                "Generate commission when you share affiliate links and drive sales from your blog posts, social, email, and more."}
            </P>
            {buttonText && buttonOnClick && (
              <PagesButton style={buttonStyle} onClick={buttonOnClick}>
                {buttonText}
              </PagesButton>
            )}
          </motion.div>
          <motion.div className="detail_right">
            <Img
              className="phone_img_bot"
              src={
                imgUrl ||
                "https://media.brandclub.com/brandclub/image_asset/detail_phone6.webp"
              }
              alt="detail_phone3_top"
            />
          </motion.div>
        </DetailContent>
      </ContentContainer>
    </Section>
  );
};
const ShopSection = () => {
  return (
    <Section>
      <ContentContainer>
        <ShophomeContent>
          <div className="shop_left">
            <Img
              className="shop_img_bot"
              src="https://media.brandclub.com/brandclub/image_asset/shop_reward2.png"
              alt="shop_reward_bg"
            />
            <Img
              className="shop_img_top"
              src="https://media.brandclub.com/brandclub/image_asset/shop_reward2_msg.webp"
              alt="shop_reward"
            />
          </div>
          <div className="shop_right">
            <div className="shop_top">
              <H2 className="header">
                The first rewards program that goes beyond shopping
              </H2>
              <P className="desc">
                Earn cash back by engaging with brands any way you choose.
              </P>
            </div>
            <div className="shop_bot">
              <ShopCard
                title="Purchases"
                imgUrl="https://media.brandclub.com/brandclub/image_asset/tag1.svg"
                desc="Get cash back on everyday purchases."
              />
              <ShopCard
                title="Membership"
                imgUrl="https://media.brandclub.com/brandclub/image_asset/tag2.svg"
                desc="Get rewards and exclusive offers when you join."
              />
              <ShopCard
                title="Referral"
                imgUrl="https://media.brandclub.com/brandclub/image_asset/tag3.svg"
                desc="Earn rewards by inviting friends to join Brandclub."
              />
              <ShopCard
                title="Surveys"
                imgUrl="https://media.brandclub.com/brandclub/image_asset/tag4.svg"
                desc="Get paid to take surveys from your favorite brands."
              />
              <ShopCard
                title="Content"
                imgUrl="https://media.brandclub.com/brandclub/image_asset/tag5.svg"
                desc="View the latest content from brands to earn rewards."
              />
              <ShopCard
                title="Affiliate"
                imgUrl="https://media.brandclub.com/brandclub/image_asset/tag7.svg"
                desc="Get paid a commission for every affiliate sale."
              />
            </div>
          </div>
        </ShophomeContent>
      </ContentContainer>
    </Section>
  );
};

const EnrichSection = () => {
  return (
    <Section>
      <ContentContainer>
        <EnrichContent>
          <div className="enrich_top">
            <div className="header">Enrich the way you shop</div>
            <div className="desc">
              Brandclub is an all-in-one shopping assistant that automatically
              saves you money and helps you make the right decision.
            </div>
          </div>
          <div className="enrich_bot">
            <div className="enrich_left">
              <Img
                className="enrich_phone_bot"
                src="https://media.brandclub.com/brandclub/image_asset/detail_phone7.png"
                alt="enrich_phone"
              />
            </div>
            <div className="enrich_right">
              <EnrichCard
                imgUrl="https://media.brandclub.com/brandclub/icons/abstract/17.svg"
                desc="Easily compare prices across retailers."
                imageHeight={28}
              />
              <EnrichCard
                imgUrl="https://media.brandclub.com/brandclub/icons/abstract/16.svg"
                desc="Get special access to deals and new product releases."
                imageHeight={33}
                style={{
                  paddingTop: 80,
                }}
              />
              <EnrichCard
                imgUrl="https://media.brandclub.com/brandclub/icons/abstract/15.svg"
                desc="Receive exclusive offers from your favorite brands."
                imageHeight={35}
                style={{
                  paddingTop: 75,
                }}
              />
              <EnrichCard
                imgUrl="https://media.brandclub.com/brandclub/icons/abstract/3.webp"
                desc="Earn rewards from your favorite online and brick-and-mortar stores."
                imageHeight={35}
                style={{
                  paddingTop: 75,
                }}
              />
            </div>
          </div>
        </EnrichContent>
      </ContentContainer>
    </Section>
  );
};

interface ShareSectionProps {
  buttonText?: string;
  buttonOnClick?: () => void;
  buttonStyle?: React.CSSProperties;
  className?: string;
  imgUrl?: string;
}
const ShareSection = ({
  buttonText,
  buttonStyle,
  buttonOnClick,
  className,
  imgUrl,
}: ShareSectionProps) => {
  return (
    <Section className={className}>
      <ContentContainer>
        <ShareContent>
          <div className="share_right">
            <H2 className="header">Make your voice heard</H2>
            <div className="desc">
              <P>
                Get paid to share your opinions, such as feedback on new
                products or packaging—make things you care about better.
              </P>
              {buttonText && buttonOnClick && (
                <PagesButton onClick={buttonOnClick} style={buttonStyle}>
                  {buttonText}
                </PagesButton>
              )}
            </div>
          </div>
          <div className="share_left">
            <Img
              className="share_sec_bot"
              src={
                imgUrl ||
                "https://media.brandclub.com/brandclub/image_asset/share_sec.png"
              }
              alt="share_sec_bg"
            />
            {!imgUrl && (
              <Img
                className="share_sec_top"
                src="https://media.brandclub.com/brandclub/image_asset/share_sec_bg.png"
                alt="share_sec"
              />
            )}
          </div>
        </ShareContent>
      </ContentContainer>
    </Section>
  );
};

const LockSection = () => {
  return (
    <Section>
      <ContentContainer>
        <LockContent>
          <Img
            src="https://media.brandclub.com/brandclub/image_asset/lock_sec.svg"
            alt="lock_sec"
            className="lock_sec_header_icon"
          />

          <H2 className="header">Your finances, safe and secure</H2>
          <P className="desc">
            Our top priority is keeping your data secure. With multiple safety
            measures, like secure 256-bit encryption and multi-factor
            authentication, we protect your info like it’s our own.
          </P>
        </LockContent>
      </ContentContainer>
    </Section>
  );
};

const AboutPage = () => {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);

  return (
    <Site
      ref={ref}
      style={{
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Helmet>
        <title>
          About - Brandclub is a shopping and rewards app that saves you money
        </title>
        <meta
          name="description"
          content="Brandclub is an all-in-one shopping and rewards app that automatically saves you money and helps you earn rewards from your favorite brands."
        />
      </Helmet>
      <HeadSection videos={ABOUT_PAGE_HEADER_VIDEOS} />
      <HomeTeachSection />
      <Detail1Section
        buttonOnClick={() => {
          navigate("/getapp");
        }}
        buttonText={"Get the app"}
        buttonStyle={{ width: 157, whiteSpace: "nowrap" }}
      />
      <Detail2Section
        buttonOnClick={() => {
          navigate("/getapp");
        }}
        buttonText={"Join now"}
        buttonStyle={{ width: 129, whiteSpace: "nowrap" }}
      />
      <Detail3Section
        buttonOnClick={() => {
          navigate("/influencer");
        }}
        buttonText={"Learn more"}
        buttonStyle={{ width: 153, whiteSpace: "nowrap" }}
      />
      <VideosSection videos={ABOUT_PAGE_VIDEOS} />
      <ShopSection />
      <EnrichSection />
      <ShareSection
        buttonOnClick={() => {
          navigate("/getapp");
        }}
        buttonText={"Join now"}
        buttonStyle={{ width: 129, whiteSpace: "nowrap" }}
      />
      <LockSection />
    </Site>
  );
};

export default AboutPage;
